import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radar, Search, Eye, Plus } from "lucide-react";
import { API, fmtUsd } from "@/lib/api";
import { formatErr } from "@/lib/auth";
import { fmtPrice } from "./CoinPicker";

const TABS = [
  { k: "all", label: "All coins" },
  { k: "tracking", label: "Tracking" },
  { k: "followable", label: "Can follow" },
];
const PAGE = 60;

function FlowCells({ s }) {
  if (!s) return <td colSpan={3} className="px-2 text-center text-[10px] text-slate-600">no flows yet</td>;
  const net = s.net_usd;
  const tone = net > 0 ? "text-[#FF3D00]" : net < 0 ? "text-[#00E676]" : "text-slate-400";
  return (
    <>
      <td className="px-2 text-right text-[#FF3D00]">{fmtUsd(s.inflow_usd)}</td>
      <td className="px-2 text-right text-[#00E676]">{fmtUsd(s.outflow_usd)}</td>
      <td className={`px-2 text-right font-bold ${tone}`}>{net > 0 ? "▼ " : net < 0 ? "▲ " : ""}{fmtUsd(Math.abs(net))}</td>
    </>
  );
}

function Action({ m, onView, onFollow, busy }) {
  if (m.flow === "followable")
    return (
      <button data-testid={`explorer-follow-${m.symbol}`} disabled={busy} onClick={() => onFollow(m.symbol)}
        className="flex items-center gap-1 rounded border border-[#00E5FF] px-2 py-0.5 text-[10px] text-[#00E5FF] transition-colors hover:bg-[#00E5FF] hover:text-[#080B10] disabled:opacity-40">
        <Plus className="h-3 w-3" /> Follow flows
      </button>
    );
  if (m.flow === "tracking")
    return <span className="flex items-center gap-1 text-[10px] text-[#00E676]"><Radar className="h-3 w-3" /> Tracking</span>;
  return <span className="text-[10px] text-slate-600" title="Not an Ethereum token — needs its own chain feed">chart only</span>;
}

export default function FlowExplorer({ stats, onView, onTracked }) {
  const [markets, setMarkets] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/markets`).then((r) => setMarkets(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);

  const statBy = useMemo(() => Object.fromEntries(stats.filter((s) => s.count).map((s) => [s.symbol, s])), [stats]);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return markets.filter((m) => (tab === "all" || m.flow === tab) &&
      (!term || m.symbol.toLowerCase().includes(term) || (m.name || "").toLowerCase().includes(term)));
  }, [markets, q, tab]);
  const counts = useMemo(() => ({ tracking: markets.filter((m) => m.flow === "tracking").length,
    followable: markets.filter((m) => m.flow === "followable").length, all: markets.length }), [markets]);

  const follow = async (sym) => {
    setBusy(sym);
    try {
      await axios.post(`${API}/tokens/follow`, { symbol: sym });
      toast.success(`Now tracking ${sym} whale flows between wallets and exchanges`);
      await load(); onTracked(); onView(sym);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(null);
  };

  return (
    <section className="panel p-4" data-testid="flow-explorer">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Coin flow explorer</h2>
          <p className="text-xs text-slate-400">Every coin in the market. Follow any Ethereum-based coin to watch whales move it between wallets and exchanges.</p>
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
          <input data-testid="explorer-search-input" value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} placeholder="Search coin or name…"
            className="w-64 rounded-md border border-[#2E3C54] bg-[#080B10] py-2 pl-8 pr-3 font-mono text-xs outline-none focus:border-[#00E5FF]" />
        </div>
      </div>
      <div className="mt-3 flex gap-1">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`explorer-tab-${t.k}`} onClick={() => { setTab(t.k); setLimit(PAGE); }} className={`chip !text-[11px] ${tab === t.k ? "active" : ""}`}>
            {t.label} <span className="opacity-60">{counts[t.k]}</span>
          </button>
        ))}
      </div>
      <div className="thin-scroll mt-3 max-h-[460px] overflow-auto rounded-md border border-[#1E2738]">
        <table className="w-full font-mono text-xs" data-testid="explorer-table">
          <thead className="sticky top-0 bg-[#161D2A] text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left">Coin</th><th className="px-2 text-right">Price</th><th className="px-2 text-right">24h</th>
              <th className="px-2 text-right">→ Exch 24h</th><th className="px-2 text-right">Exch → 24h</th><th className="px-2 text-right">Net</th>
              <th className="px-2 text-left">Flow</th><th className="px-2" />
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, limit).map((m) => (
              <tr key={m.pair || m.cg_id} data-testid={`explorer-row-${m.symbol}`} className="border-t border-[#1E2738] transition-colors hover:bg-[#161D2A]">
                <td className="px-2 py-1.5">
                  <span className="font-semibold text-slate-100">{m.symbol}</span>
                  <span className="ml-2 hidden text-[10px] text-slate-500 md:inline">{m.name}</span>
                </td>
                <td className="px-2 text-right text-slate-300">${fmtPrice(m.price)}</td>
                <td className={`px-2 text-right ${m.change_pct >= 0 ? "text-[#00E676]" : "text-[#FF3D00]"}`}>{m.change_pct >= 0 ? "+" : ""}{m.change_pct}%</td>
                <FlowCells s={statBy[m.symbol]} />
                <td className="px-2"><Action m={m} onFollow={follow} busy={busy === m.symbol} /></td>
                <td className="px-2 text-right">
                  <button data-testid={`explorer-view-${m.symbol}`} onClick={() => onView(m.symbol, m)} title="Show on chart" className="text-slate-500 hover:text-[#00E5FF]">
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="p-6 text-center text-xs text-slate-500" data-testid="explorer-empty">No coins match.</p>}
      </div>
      {rows.length > limit && (
        <button data-testid="explorer-load-more" onClick={() => setLimit((l) => l + PAGE)} className="mt-2 w-full rounded-md border border-[#1E2738] py-2 font-mono text-xs text-slate-400 hover:text-white">
          Show more ({rows.length - limit} left)
        </button>
      )}
      <p className="mt-2 font-mono text-[10px] text-slate-600">▼ net into exchanges (sell pressure) · ▲ net out of exchanges (accumulation) · flows count transfers ≥ $500K to/from labelled exchange wallets</p>
    </section>
  );
}
